import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '@clerk/clerk-react';
import QRCode from 'qrcode';
import RewardsCardModal from '../components/RewardsCardModal';

/**
 * Public menu page at /m/:slug. Uses CartContext for cart state and persistence.
 */
export default function Menu() {
  const { slug } = useParams();
  const { getToken, isSignedIn } = useAuth();
  const cart = useCart();
  const [business, setBusiness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const categoryScrollRef = useRef(null);
  const itemsScrollRef = useRef(null);
  const [categoriesOverflow, setCategoriesOverflow] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [rewardsConfig, setRewardsConfig] = useState(null);
  const [rewardsProgress, setRewardsProgress] = useState(null);
  const [rewardsCardPhase, setRewardsCardPhase] = useState('idle'); // 'idle' | 'showing' | 'morphing' | 'done'
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [rewardsModalJustEarned, setRewardsModalJustEarned] = useState(0);
  const [hasRefParam, setHasRefParam] = useState(false);
  const refParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('ref') : null;
  const callbackUrl = `${window.location.origin}/auth/callback?type=customer${business?.id ? `&businessId=${business.id}` : ''}${refParam ? `&ref=${encodeURIComponent(refParam)}` : ''}`;

  useEffect(() => {
    if (slug) localStorage.setItem('gng_lastVisitedMenu', slug);
  }, [slug]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setHasRefParam(!!params.get('ref'));
  }, []);

  // Handle ref param - store for checkout attribution, call record-ref-visit (requires auth — per-visitor 5 pts)
  useEffect(() => {
    if (!business?.id || !refParam) return;
    sessionStorage.setItem(`gng_ref_${business.id}`, refParam);
    if (!isSignedIn) return; // must be signed in so backend can identify visitor and prevent gaming
    api(`/api/businesses/${business.id}/record-ref-visit?ref=${encodeURIComponent(refParam)}`, {
      getToken,
      method: 'POST',
    }).catch(() => {});
  }, [business?.id, refParam, isSignedIn, getToken]);

  // Pre-fill phone from saved profile when signed in
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await api('/api/customers/me', { getToken });
      if (!cancelled && res.ok) {
        const d = await res.json();
        if (d.phone) setCustomerPhone(d.phone);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const bizRes = await api(`/api/businesses/by-slug/${encodeURIComponent(slug)}`);
        if (!bizRes.ok) {
          if (bizRes.status === 404) setError('Menu not found.');
          else setError('Failed to load menu.');
          return;
        }
        const bizData = await bizRes.json();
        if (cancelled) return;
        setBusiness(bizData);
        cart.setBusinessId(bizData.id);
        // Track visited menu for dashboard
        try {
          const stored = localStorage.getItem('gng_visitedMenus');
          const list = stored ? JSON.parse(stored) : [];
          const entry = { slug: bizData.slug, name: bizData.name, businessId: bizData.id };
          const filtered = list.filter((m) => m.slug !== entry.slug);
          localStorage.setItem('gng_visitedMenus', JSON.stringify([entry, ...filtered].slice(0, 20)));
        } catch (_) { /* ignore */ }

        const [menuRes, rewardsRes] = await Promise.all([
          api(`/api/businesses/${bizData.id}/menu`),
          api(`/api/businesses/${bizData.id}/rewards-config`),
        ]);
        if (!menuRes.ok) return;
        const menuData = await menuRes.json();
        if (cancelled) return;
        setCategories(menuData);
        if (rewardsRes.ok) {
          const rewardsData = await rewardsRes.json();
          setRewardsConfig(rewardsData);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load menu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
      if (!slug) cart.setBusinessId(null);
    };
  }, [slug, cart.setBusinessId]);

  useEffect(() => {
    if (!business?.id || !getToken) return;
    let cancelled = false;
    api(`/api/customers/me/rewards?businessId=${business.id}`, { getToken }).then((res) => {
      if (!cancelled && res.ok) return res.json();
    }).then((d) => {
      if (!cancelled && d) setRewardsProgress(d);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [business?.id, getToken]);

  // If user just earned points (e.g. from pickup), highlight in the already-visible modal
  useEffect(() => {
    if (!business?.id || !getToken) return;
    api(`/api/customers/me/rewards/latest-award?businessId=${business.id}`, { getToken }).then((res) => {
      if (!res.ok) return;
      return res.json();
    }).then((d) => {
      if (d?.hasNewPoints && d.pointsAwarded) {
        setRewardsProgress((p) => p ? { ...p, points: d.newTotalPoints } : { points: d.newTotalPoints, pointsCap: d.pointsCap });
        setRewardsModalJustEarned(d.pointsAwarded);
      }
    }).catch(() => {});
  }, [business?.id, getToken]);

  // Show rewards card (with coins) on every menu load when signed in + has rewards
  useEffect(() => {
    if (!rewardsConfig || !isSignedIn) return;
    setRewardsCardPhase('showing');
    setShowRewardsModal(true);
  }, [rewardsConfig, isSignedIn]);

  const handleRewardsModalClose = useCallback(() => {
    setShowRewardsModal(false);
    setRewardsModalJustEarned(0);
    setRewardsCardPhase('done');
  }, []);

  // Default to first category when categories load
  useEffect(() => {
    if (categories.length > 0 && (selectedCategoryId === null || !categories.some((c) => c.id === selectedCategoryId))) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  // Measure whether category tabs overflow the container
  useEffect(() => {
    const el = categoryScrollRef.current;
    if (!el || categories.length === 0) return;

    const measure = () => {
      const buttons = el.querySelectorAll('[data-category-id]');
      let totalWidth = 0;
      buttons.forEach((b, i) => {
        totalWidth += b.offsetWidth;
        if (i < buttons.length - 1) totalWidth += 8;
      });
      setCategoriesOverflow(totalWidth > el.clientWidth);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [categories]);

  const addToCart = useCallback((item, addons = [], notes = '', modifiers = []) => {
    const unitCents = Math.round(item.price * 100);
    const addonCents = addons.reduce((s, a) => s + Math.round(a.price * 100), 0);
    cart.addItem({
      id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      menuItemId: item.id,
      name: item.name,
      unitPriceCents: unitCents + addonCents,
      quantity: 1,
      notes: notes || '',
      addons: addons.map((a) => ({ id: a.id, name: a.name, price: a.price })),
      modifiers: modifiers.map((m) => ({
        groupId: m.groupId,
        groupName: m.groupName,
        optionId: m.optionId,
        optionName: m.optionName,
      })),
    });
  }, [cart]);

  const handleCheckout = useCallback(async () => {
    if (!business || cart.items.length === 0) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const refToken = sessionStorage.getItem(`gng_ref_${business.id}`);
      const body = {
        businessId: business.id,
        items: cart.items.map((i) => ({
          menuItemId: i.menuItemId,
          quantity: i.quantity,
          notes: i.notes || undefined,
          addonIds: (i.addons || []).map((a) => a.id),
          modifiers: (i.modifiers || []).map((m) => ({ groupId: m.groupId, optionId: m.optionId })),
        })),
        cancelUrl: window.location.href,
        customerPhone: customerPhone.trim() || undefined,
        referralShareToken: refToken || undefined,
        rewardClaimId: cart.appliedReward?.businessId === business.id ? cart.appliedReward.claimId : undefined,
      };
      const res = await api('/api/orders/checkout', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      if (data.url) {
        if (customerPhone.trim()) {
          try {
            await api('/api/customers/me', {
              getToken,
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: customerPhone.trim() }),
            });
          } catch (_) { /* save for next time, don't block redirect */ }
        }
        cart.clear();
        setCartOpen(false);
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setCheckoutError(err.message);
    } finally {
      setCheckoutLoading(false);
    }
  }, [business, cart, getToken, customerPhone]);

  const handleShareClick = useCallback(() => {
    setShareModalOpen(true);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading menu…</p>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <p className="text-red-600 dark:text-red-400">{error || 'Menu not found.'}</p>
        <Link to="/" className="text-indigo-600 dark:text-indigo-400 hover:underline">Go home</Link>
      </div>
    );
  }

  const hasItems = cart.itemCount > 0;
  const isCurrentBusiness = cart.businessId === business.id;

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const resolveImageUrl = (url) =>
    url?.startsWith('/') && baseUrl ? `${baseUrl.replace(/\/$/, '')}${url}` : url;

  const fontName = business.menuFont?.split(',')[0]?.replace(/"/g, '').trim();
  const googleFonts = ['Inter', 'Roboto', 'Open Sans', 'Playfair Display', 'Lora', 'Montserrat', 'Oswald'];
  const needsGoogleFont = fontName && googleFonts.includes(fontName);

  const hasCustomTheme = business.menuBackgroundColor || business.menuTextColor || business.menuFont;
  const menuStyle = {
    backgroundColor: business.menuBackgroundColor || (hasCustomTheme ? undefined : '#faf9f7'),
    fontFamily: business.menuFont || undefined,
    color: business.menuTextColor || (hasCustomTheme ? undefined : '#1a1a1a'),
  };

  const bgImageUrl = business.menuBackgroundImageUrl
    ? resolveImageUrl(business.menuBackgroundImageUrl)
    : null;

  return (
    <>
      {needsGoogleFont && (
        <link
          href={`https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`}
          rel="stylesheet"
        />
      )}
      <div className="menu-page flex flex-col h-screen overflow-hidden relative pt-14" style={menuStyle}>
      {bgImageUrl && (
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${bgImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* Visitor ref banner - only when signed OUT (encourages sign-up); hide once signed in */}
      {hasRefParam && rewardsConfig && !isSignedIn && (
        <button
          type="button"
          onClick={handleShareClick}
          className="flex-shrink-0 w-full px-4 py-3 text-center bg-gradient-to-r from-amber-400/90 to-amber-500/90 dark:from-amber-600/90 dark:to-amber-700/90 border-b border-amber-600/30 hover:opacity-95 transition-opacity"
        >
          <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
            🎁 Earn {rewardsConfig.pointsPerShare ?? 5} pts for visiting!
          </p>
          <p className="text-xs text-blue-600/90 dark:text-blue-400/90 mt-0.5">
            Sign up & order to earn {rewardsConfig.pointsPerReferral ?? 10} more — free drinks, discounts & more
          </p>
        </button>
      )}

      {/* Rewards bar - only after card morph animation completes */}
      <SignedIn>
        {rewardsConfig && rewardsCardPhase === 'done' && (
          <button
            type="button"
            onClick={handleShareClick}
            className="flex-shrink-0 w-full px-4 py-2 text-center bg-amber-50/90 dark:bg-amber-900/20 border-b border-amber-200/60 dark:border-amber-800/40 hover:opacity-95 transition-opacity animate-fade-in"
          >
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {rewardsProgress?.points ?? 0} / {rewardsConfig.pointsCap ?? 100} pts · Share to earn more
            </span>
            <div className="mt-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-600 overflow-hidden mx-auto max-w-xs">
              <div
                className="h-full rounded-full bg-amber-500"
                style={{ width: `${Math.min(100, ((rewardsProgress?.points ?? 0) / (rewardsConfig.pointsCap ?? 100)) * 100)}%` }}
              />
            </div>
          </button>
        )}
      </SignedIn>

      {/* Sticky top: header + category tabs */}
      <div className="flex-shrink-0 relative z-10">
        <div className="menu-content-wrap pt-4">
          <header className="flex flex-col">
            <div className="flex flex-col items-center justify-center pt-4 pb-4 border-b border-slate-200/60 dark:border-slate-700/60 text-center">
              {business.logoUrl && (
                <img src={resolveImageUrl(business.logoUrl)} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-white/80 shadow-md mb-3" />
              )}
              <h1 className="menu-heading text-3xl md:text-4xl font-bold leading-tight break-words w-full px-2" style={{ color: business.menuTextColor || '#1a1a1a' }}>
                {business.name}
              </h1>
              <span
                className={`inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full tracking-wide ${
                  business.isOpen
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-slate-200/80 text-slate-600 dark:bg-slate-600 dark:text-slate-300'
                }`}
              >
                {business.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
          </header>
        </div>
        <div className="menu-content-wrap py-3">
          <div
            ref={categoryScrollRef}
            className={`flex gap-2 overflow-x-auto pb-1 scrollbar-hide category-tabs-scroll ${categoriesOverflow ? '' : 'justify-center'}`}
            style={categoriesOverflow ? { scrollSnapType: 'x mandatory', scrollPaddingInline: '1rem' } : undefined}
          >
            {categories.map((cat) => {
                const isSelected = selectedCategoryId === cat.id;
                const activeBg = business.menuCategoryTileColor || '#c9a227';
                const activeText = business.menuCategoryTextColor || '#1a1a1a';
                const inactiveBg = business.menuCategoryInactiveTileColor || '#f8fafc';
                const inactiveText = business.menuCategoryTextColor || business.menuTextColor || '#475569';
                const glowColor = business.menuCategoryTileColor || '#c9a227';
                return (
                  <button
                    key={cat.id}
                    type="button"
                    data-category-id={cat.id}
                    onClick={() => { setSelectedCategoryId(cat.id); if (itemsScrollRef.current) itemsScrollRef.current.scrollTop = 0; }}
                    className="flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap snap-center border"
                    style={isSelected
                      ? {
                          backgroundColor: activeBg,
                          color: activeText,
                          borderColor: glowColor,
                          boxShadow: `0 0 0 2px ${glowColor}, 0 0 12px ${glowColor}66`,
                        }
                      : { backgroundColor: inactiveBg, color: inactiveText, borderColor: '#e2e8f0', boxShadow: 'none' }
                    }
                  >
                    {cat.name}
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      {/* Scrollable items area */}
      <div ref={itemsScrollRef} className="flex-1 overflow-y-auto relative z-10 pb-20">
        <div className="menu-content-wrap py-4 space-y-5">
          {categories
            .filter((cat) => cat.id === selectedCategoryId)
            .map((cat) => (
              <div key={cat.id} className="space-y-5">
                {cat.items.map((item, i) => (
                  <div
                    key={item.id}
                    className="animate-slide-in-item"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <MenuItemRow
                      item={item}
                      isOpen={business.isOpen}
                      soldOut={item.soldOut}
                      onAddToCart={addToCart}
                      resolveImageUrl={resolveImageUrl}
                      imagePosition={business.menuImagePosition || 'top'}
                      menuTextColor={business.menuTextColor}
                    />
                  </div>
                ))}
              </div>
            ))}
        </div>
      </div>

      {/* Bottom navbar – app-style tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 safe-area-inset-bottom z-30">
        <div className="max-w-[640px] mx-auto flex items-center justify-around h-16 px-2">
          <button
            type="button"
            onClick={() => isCurrentBusiness && setCartOpen(true)}
            className="flex flex-col items-center justify-center flex-1 min-w-0 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            aria-label="Cart"
          >
            <span className="relative">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {hasItems && isCurrentBusiness && (
                <span className="absolute -top-2 -right-3 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[#c9a227] text-[#1a1a1a]">
                  {cart.itemCount > 99 ? '99+' : cart.itemCount}
                </span>
              )}
            </span>
            <span className="text-xs mt-0.5 font-medium">Cart</span>
          </button>
          <button
            type="button"
            onClick={handleShareClick}
            className="flex flex-col items-center justify-center flex-1 min-w-0 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            aria-label="Share"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="text-xs mt-0.5 font-medium">Share</span>
          </button>
          <SignedOut>
            <SignInButton mode="redirect" forceRedirectUrl={callbackUrl}>
              <button
                type="button"
                className="flex flex-col items-center justify-center flex-1 min-w-0 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                aria-label="Sign in"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span className="text-xs mt-0.5 font-medium">Sign in</span>
              </button>
            </SignInButton>
          </SignedOut>
          <SignedIn>
            <Link
              to="/profile"
              className="flex flex-col items-center justify-center flex-1 min-w-0 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              aria-label="Dashboard"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="text-xs mt-0.5 font-medium">Dashboard</span>
            </Link>
          </SignedIn>
        </div>
      </nav>

      {/* Share modal – QR code + copy link (with ref when signed in) */}
      {shareModalOpen && (
        <ShareModal
          businessId={business?.id}
          slug={business?.slug}
          getToken={getToken}
          onClose={() => setShareModalOpen(false)}
        />
      )}

      {/* Rewards card modal - shows on load, morphs into banner in 5s or on Got it */}
      {showRewardsModal && rewardsConfig && (
        <RewardsCardModal
          businessId={business?.id}
          points={rewardsProgress?.points ?? 0}
          pointsCap={rewardsConfig.pointsCap ?? 100}
          pointsJustEarned={rewardsModalJustEarned}
          referralCount={rewardsProgress?.referralCount}
          referralsNeeded={rewardsConfig.referralsNeededForReward}
          availableRewards={rewardsProgress?.availableRewards}
          canClaim={rewardsProgress?.canClaim}
          rewardsClaimedCount={rewardsProgress?.rewardsClaimedCount}
          lastClaimedRewards={rewardsProgress?.lastClaimedRewards}
          getToken={getToken}
          onClose={handleRewardsModalClose}
          onClaimSuccess={() => {
            api(`/api/customers/me/rewards?businessId=${business?.id}`, { getToken })
              .then((res) => res.ok ? res.json() : null)
              .then((d) => d && setRewardsProgress(d))
              .catch(() => {});
          }}
          showCoins={true}
        />
      )}

      {/* Cart panel (slide-in, shown when cart icon or bar clicked) */}
      {cartOpen && isCurrentBusiness && (
        <CartPanel
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              cart={cart}
              business={business}
              rewardsProgress={rewardsProgress}
              categories={categories}
              onClose={() => setCartOpen(false)}
              onCheckout={handleCheckout}
              checkoutLoading={checkoutLoading}
              checkoutError={checkoutError}
              callbackUrl={callbackUrl}
              getToken={getToken}
              onRewardsRefresh={() => {
                api(`/api/customers/me/rewards?businessId=${business?.id}`, { getToken })
                  .then((res) => res.ok ? res.json() : null)
                  .then((d) => d && setRewardsProgress(d))
                  .catch(() => {});
              }}
            />
      )}
    </div>
    </>
  );
}

const SHARE_POLL_INTERVAL_MS = 5000;

function ShareModal({ businessId, slug, getToken, onClose }) {
  const [menuUrl, setMenuUrl] = useState(slug ? `${window.location.origin}/m/${slug}` : '');
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(!!(businessId && getToken));
  const [rewardsLive, setRewardsLive] = useState(null);

  useEffect(() => {
    if (!businessId) {
      setMenuUrl(slug ? `${window.location.origin}/m/${slug}` : '');
      setLoading(false);
      return;
    }
    if (!getToken) {
      setMenuUrl(slug ? `${window.location.origin}/m/${slug}` : '');
      setLoading(false);
      return;
    }
    api('/api/customers/me/share-link', {
      getToken,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ businessId, source: 'qr' }),
    }).then((res) => {
      if (res.ok) return res.json();
      return null;
    }).then((d) => {
      if (d?.shareUrl) {
        setMenuUrl(d.shareUrl);
      } else {
        setMenuUrl(slug ? `${window.location.origin}/m/${slug}` : '');
      }
    }).catch(() => {
      setMenuUrl(slug ? `${window.location.origin}/m/${slug}` : '');
    }).finally(() => setLoading(false));
  }, [businessId, slug, getToken]);

  useEffect(() => {
    if (!menuUrl) return;
    QRCode.toDataURL(menuUrl, { width: 220, margin: 2 }).then(setQrDataUrl);
  }, [menuUrl]);

  // Poll rewards while modal is open (order-queue style live updates)
  useEffect(() => {
    if (!businessId || !getToken || loading) return;
    const fetchRewards = () => {
      api(`/api/customers/me/rewards?businessId=${businessId}`, { getToken })
        .then((res) => (res.ok ? res.json() : null))
        .then(setRewardsLive)
        .catch(() => {});
    };
    fetchRewards();
    const interval = setInterval(fetchRewards, SHARE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [businessId, getToken, loading]);

  const handleCopyLink = async () => {
    if (!menuUrl) return;
    try {
      await navigator.clipboard.writeText(menuUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const displayUrl = menuUrl || (slug ? `${window.location.origin}/m/${slug}` : '');

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      aria-hidden
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Share menu</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {rewardsLive && (
          <div className="mb-4 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Your points: {rewardsLive.points ?? 0} / {rewardsLive.pointsCap ?? 100}
            </p>
            <div className="mt-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-600 overflow-hidden">
              <div
                className="h-full rounded-full bg-amber-500 transition-all duration-300"
                style={{ width: `${Math.min(100, ((rewardsLive.points ?? 0) / (rewardsLive.pointsCap ?? 100)) * 100)}%` }}
              />
            </div>
          </div>
        )}
        {loading && <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-4">Loading…</p>}
        {!loading && qrDataUrl && (
          <div className="flex justify-center mb-4">
            <img src={qrDataUrl} alt="Menu QR code" className="w-52 h-52" />
          </div>
        )}
        <button
          type="button"
          onClick={handleCopyLink}
          disabled={!displayUrl}
          className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium transition-colors"
        >
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

const PERCENT_OFF_TYPES = ['10_percent_off', 'custom_percent'];

function UseRewardModal({ rewardsProgress, business, cart, categories, getToken, onClose, onApplySuccess }) {
  const [claiming, setClaiming] = useState(false);
  const [claimView, setClaimView] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);

  const unredeemed = rewardsProgress?.unredeemedClaim;
  const canClaim = rewardsProgress?.canClaim && !unredeemed;
  const availableRewards = rewardsProgress?.availableRewards ?? [];

  const findMenuItem = (menuItemId) => {
    if (!menuItemId || !categories?.length) return null;
    for (const cat of categories) {
      const item = cat.items?.find((i) => i.id === menuItemId);
      if (item) return item;
    }
    return null;
  };

  const handleApply = async (claim) => {
    if (!claim || !business) return;
    const isPercentOff = PERCENT_OFF_TYPES.includes(claim.rewardType);
    if (isPercentOff) {
      cart.setAppliedReward({
        claimId: claim.id,
        businessId: business.id,
        rewardType: claim.rewardType,
        discountPercent: claim.customPercent ?? 10,
        label: claim.label,
      });
    } else if (claim.menuItemId) {
      const item = findMenuItem(claim.menuItemId);
      if (item) {
        cart.addItem({
          id: `line-reward-${Date.now()}`,
          menuItemId: item.id,
          name: item.name,
          unitPriceCents: 0,
          quantity: 1,
          notes: '',
          addons: [],
          modifiers: [],
          isReward: true,
        });
        cart.setAppliedReward({
          claimId: claim.id,
          businessId: business.id,
          rewardType: claim.rewardType,
          freeMenuItemId: claim.menuItemId,
          label: claim.label,
        });
      } else {
        alert('Menu item not found. Please add it from the menu.');
        return;
      }
    } else {
      alert('This reward type is not supported in cart.');
      return;
    }
    onApplySuccess?.();
  };

  const handleClaimThenApply = async () => {
    if (!selectedReward || !business) return;
    setClaiming(true);
    try {
      const res = await api('/api/customers/me/rewards/claim', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId: business.id,
          rewardType: selectedReward.type,
          menuItemId: selectedReward.menuItemId ?? undefined,
          customPercent: selectedReward.customPercent ?? undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Claim failed');
      const claim = data.claim ? { id: data.claim.id, ...selectedReward, label: selectedReward.label, menuItemId: selectedReward.menuItemId, customPercent: selectedReward.customPercent } : null;
      if (claim) await handleApply(claim);
      else onApplySuccess?.();
    } catch (err) {
      alert(err.message || 'Claim failed');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-xl animate-slide-up">
        <div className="p-5">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Use reward</h3>
          {unredeemed ? (
            <>
              <p className="text-slate-700 dark:text-slate-300 mb-4">{unredeemed.label}</p>
              <button
                type="button"
                onClick={() => handleApply(unredeemed)}
                className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
              >
                Apply to this order
              </button>
            </>
          ) : canClaim && !claimView ? (
            <>
              <p className="text-slate-600 dark:text-slate-400 mb-4">Pick your reward:</p>
              <div className="space-y-2">
                {availableRewards.map((r) => (
                  <button
                    key={`${r.type}-${r.menuItemId ?? ''}`}
                    type="button"
                    onClick={() => { setSelectedReward(r); setClaimView(true); }}
                    className="block w-full py-3 rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left px-4 font-medium"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </>
          ) : canClaim && claimView && selectedReward ? (
            <>
              <p className="text-slate-600 dark:text-slate-400 mb-4">Claim & apply: {selectedReward.label}</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setClaimView(false); setSelectedReward(null); }} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleClaimThenApply}
                  disabled={claiming}
                  className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
                >
                  {claiming ? 'Claiming…' : 'Claim & apply'}
                </button>
              </div>
            </>
          ) : null}
          <button type="button" onClick={onClose} className="mt-4 w-full py-2 text-slate-500 dark:text-slate-400 hover:underline">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function CartPanel({ cart, business, onClose, onCheckout, checkoutLoading, checkoutError, callbackUrl, customerPhone, setCustomerPhone, rewardsProgress, categories = [], getToken, onRewardsRefresh }) {
  const [useRewardOpen, setUseRewardOpen] = useState(false);
  const canUseReward = (rewardsProgress?.unredeemedClaim || rewardsProgress?.canClaim) && cart.items.length > 0 && cart.businessId === business?.id;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-slate-800 rounded-t-2xl shadow-[0_-8px_40px_rgba(0,0,0,0.12)] z-50 max-h-[85vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <h2 className="menu-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Your order</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4 max-w-[640px] mx-auto w-full">
          {cart.items.length > 0 && (
            <div className="pb-4 border-b border-slate-100 dark:border-slate-700">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Phone number (for order ready text) *
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-base focus:ring-2 focus:ring-[#c9a227]/40 focus:border-[#c9a227]/60 outline-none transition-shadow"
              />
            </div>
          )}
          {cart.items.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-center py-12 text-base">
              Your cart is empty. Add items from the menu.
            </p>
          ) : cart.items.map((line) => {
              const isReward = line.isReward || (line.unitPriceCents === 0 && cart.appliedReward?.freeMenuItemId === line.menuItemId);
              return (
            <div
              key={line.id}
              className="flex justify-between items-start gap-4 py-3 border-b border-slate-100 dark:border-slate-700/50 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 dark:text-slate-100">
                  {line.name}
                  {isReward && (
                    <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">(reward)</span>
                  )}
                </p>
                {line.modifiers?.length > 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    {line.modifiers.map((m) => `${m.groupName}: ${m.optionName}`).join(' · ')}
                  </p>
                )}
                {line.addons?.length > 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    + {line.addons.map((a) => a.name).join(', ')}
                  </p>
                )}
                {line.notes && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 italic mt-0.5">"{line.notes}"</p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  {isReward ? (
                    <>
                      <span className="text-sm text-slate-500 dark:text-slate-400">1 (included)</span>
                      <button
                        type="button"
                        onClick={() => { cart.removeItem(line.id); cart.clearAppliedReward(); }}
                        className="ml-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => cart.removeItem(line.id)}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <p className="font-semibold text-slate-900 dark:text-slate-100 flex-shrink-0 tabular-nums">
                {line.unitPriceCents === 0 ? 'Free' : `$${((line.unitPriceCents * line.quantity) / 100).toFixed(2)}`}
              </p>
            </div>
          ); })}
        </div>
        <div className="p-5 border-t border-slate-100 dark:border-slate-700 space-y-3 bg-slate-50/50 dark:bg-slate-900/30 max-w-[640px] mx-auto w-full">
          {cart.items.length > 0 && (
          <>
            <div className="flex justify-between text-slate-700 dark:text-slate-300">
              <span>Subtotal</span>
              <span className="tabular-nums">${((cart.subtotalCents ?? cart.totalCents) / 100).toFixed(2)}</span>
            </div>
            {cart.appliedReward && cart.discountCents > 0 && (
              <div className="flex justify-between text-amber-700 dark:text-amber-300">
                <span>Reward ({cart.appliedReward.label})</span>
                <span className="tabular-nums">-${(cart.discountCents / 100).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-semibold">
              <span className="text-slate-900 dark:text-slate-100">Total</span>
              <span className="text-slate-900 dark:text-slate-100 tabular-nums">${(cart.totalCents / 100).toFixed(2)}</span>
            </div>
          </>
          )}
          {canUseReward && (
            <button
              type="button"
              onClick={() => setUseRewardOpen(true)}
              className="w-full py-3 rounded-xl border-2 border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 font-medium min-h-[44px]"
            >
              Use reward
            </button>
          )}
          {useRewardOpen && createPortal(
            <UseRewardModal
              rewardsProgress={rewardsProgress}
              business={business}
              cart={cart}
              categories={categories}
              getToken={getToken}
              onClose={() => setUseRewardOpen(false)}
              onApplySuccess={() => { setUseRewardOpen(false); onRewardsRefresh?.(); }}
            />,
            document.body
          )}
          {cart.items.length > 0 && !business.isOpen && (
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 rounded-xl px-4 py-3">
              We're closed. Check back during our operating hours.
            </p>
          )}
          {checkoutError && (
            <p className="text-sm text-red-600 dark:text-red-400">{checkoutError}</p>
          )}
          {cart.items.length > 0 && (
          <SignedIn>
            {customerPhone.replace(/\D/g, '').length < 10 && (
              <p className="text-sm text-amber-700 dark:text-amber-300">Enter your phone number above to receive a text when your order is ready.</p>
            )}
            <button
              type="button"
              onClick={onCheckout}
              disabled={checkoutLoading || !business.isOpen || customerPhone.replace(/\D/g, '').length < 10}
              className="menu-cta-btn w-full py-3.5 rounded-xl font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checkoutLoading ? 'Processing…' : 'Checkout'}
            </button>
          </SignedIn>
          )}
          {cart.items.length > 0 && (
          <SignedOut>
            <SignInButton mode="redirect" forceRedirectUrl={callbackUrl}>
              <button className="menu-cta-btn w-full py-3.5 rounded-xl font-semibold text-base">
                Sign in to checkout
              </button>
            </SignInButton>
          </SignedOut>
          )}
        </div>
      </div>
    </>
  );
}

function MenuItemRow({ item, isOpen, soldOut, onAddToCart, resolveImageUrl, imagePosition, menuTextColor }) {
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [notes, setNotes] = useState('');
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const imageSrc = resolveImageUrl ? resolveImageUrl(item.imageUrl) : item.imageUrl;
  const pos = imagePosition || 'top';
  const textColor = menuTextColor || '#1a1a1a';

  const modifierGroups = Array.isArray(item.modifierGroups) ? item.modifierGroups : [];

  const toggleAddon = (addon) => {
    setSelectedAddons((prev) =>
      prev.some((a) => a.id === addon.id)
        ? prev.filter((a) => a.id !== addon.id)
        : [...prev, addon]
    );
  };

  const handleModifierChange = (groupId, optionIdStr) => {
    const optionId = parseInt(optionIdStr, 10);
    setSelectedModifiers((prev) => ({ ...prev, [groupId]: Number.isNaN(optionId) ? undefined : optionId }));
  };

  const modifiersComplete = modifierGroups.every((g) => selectedModifiers[g.id] != null);

  const handleAdd = () => {
    if (!modifiersComplete) return;
    const modifierSelections = modifierGroups.map((g) => {
      const optionId = selectedModifiers[g.id];
      const option = g.options?.find((o) => o.id === optionId);
      return {
        groupId: g.id,
        groupName: g.name,
        optionId,
        optionName: option?.name || '',
      };
    });
    onAddToCart(item, selectedAddons, notes, modifierSelections);
    setSelectedAddons([]);
    setNotes('');
    setSelectedModifiers({});
  };

  const isVertical = pos === 'top' || pos === 'bottom';
  const isHorizontal = pos === 'left' || pos === 'right';
  const canAdd = isOpen && !soldOut && modifiersComplete;

  const imageBlock = imageSrc ? (
    <div className={isVertical ? 'w-full h-40 md:h-48' : 'flex-shrink-0 w-28 h-28 md:w-32 md:h-32'}>
      <img src={imageSrc} alt="" className="menu-item-image w-full h-full" />
    </div>
  ) : null;

  const contentBlock = (
    <div className={`flex flex-col ${isVertical ? 'p-5' : 'flex-1 min-w-0 py-3'}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="menu-heading text-lg font-semibold leading-tight" style={{ color: textColor }}>
          {item.name}
        </h3>
        {soldOut && (
          <span className="flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
            Sold out
          </span>
        )}
      </div>
      {item.description && (
        <p className="text-sm mt-1 leading-relaxed line-clamp-2 opacity-80" style={{ color: textColor }}>
          {item.description}
        </p>
      )}
      <p className="text-base font-semibold mt-2 tabular-nums" style={{ color: textColor }}>
        ${Number(item.price).toFixed(2)}
      </p>
      {modifierGroups.length > 0 && (
        <div className="mt-3 space-y-2">
          {modifierGroups.map((g) => {
            const value = selectedModifiers[g.id] ?? '';
            return (
              <div key={g.id}>
                <label className="text-xs font-medium opacity-75 uppercase tracking-wider block mb-1" style={{ color: textColor }}>
                  {g.name} <span className="text-red-500 normal-case">*</span>
                </label>
                <select
                  required
                  value={value}
                  onChange={(e) => handleModifierChange(g.id, e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-[#c9a227]/40 focus:border-[#c9a227]/60 outline-none transition-shadow"
                >
                  <option value="" disabled>Choose {g.name.toLowerCase()}…</option>
                  {g.options?.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
      {item.addons?.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <p className="text-xs font-medium opacity-75 uppercase tracking-wider" style={{ color: textColor }}>Add-ons</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {item.addons.map((a) => (
              <label key={a.id} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedAddons.some((x) => x.id === a.id)}
                  onChange={() => toggleAddon(a)}
                  className="addon-checkbox rounded border-slate-300"
                />
                <span className="text-sm opacity-90 group-hover:opacity-100" style={{ color: textColor }}>
                  {a.name} <span className="opacity-75">+${Number(a.price).toFixed(2)}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Notes (e.g. no onions)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="menu-notes-input flex-1 min-w-[140px] px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/80 placeholder:text-slate-400 focus:ring-2 focus:ring-[#c9a227]/40 focus:border-[#c9a227]/60 outline-none transition-shadow"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className="menu-cta-btn px-4 py-2.5 text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {soldOut ? 'Sold out' : !modifiersComplete ? 'Choose options' : 'Add to cart'}
        </button>
      </div>
    </div>
  );

  const cardClass = `menu-item-card rounded-xl overflow-hidden ${soldOut ? 'opacity-60 grayscale' : ''}`;
  if (isHorizontal) {
    return (
      <article className={`${cardClass} flex gap-4 p-4`}>
        {pos === 'left' && imageBlock}
        {contentBlock}
        {pos === 'right' && imageBlock}
      </article>
    );
  }

  return (
    <article className={`${cardClass} flex flex-col`}>
      {pos === 'top' && imageBlock}
      {contentBlock}
      {pos === 'bottom' && imageBlock}
    </article>
  );
}
